import { useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Input, DatePicker, notification } from 'antd';

import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import useModalStore from 'stores/useModal';
import useBookingStore from 'stores/useBooking';

import { ReactComponent as IconClose } from 'assets/images/close.svg';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ParamsCreateBookingRemind } from '@/services/api/type/booking.type';
import API from 'services/api/index';
import dayjs from 'dayjs';

function BookingRemindModal() {
  const { t } = useTranslation();
  const [form] = Form.useForm<ParamsCreateBookingRemind>();

  const { bookingLineId } = useBookingStore();
  const { isOpenBookingRemind, setIsOpenBookingRemind } = useModalStore();

  const { data: bookingLineDetail, isLoading } = useGetBookingDetail(bookingLineId);

  const { mutateAsync: mutateCreateBookingRemind } = useMutation({
    mutationFn: (params: ParamsCreateBookingRemind) => API.booking.createBookingRemind(params)
  });

  useEffect(() => {
    if (bookingLineDetail && isOpenBookingRemind) {
      form.setFieldsValue({
        booking_line_id: bookingLineDetail.booking_line_id
      });
    }
  }, [bookingLineDetail, isOpenBookingRemind, form]);

  const handleCloseModal = () => {
    setIsOpenBookingRemind(false);
    form.resetFields();
  };

  const handleFinish = async (values: ParamsCreateBookingRemind) => {
    try {
      await mutateCreateBookingRemind({
        ...values,
        date: dayjs(values.date).format('YYYY-MM-DD')
      });
      handleCloseModal();
      notification.success({
        message: t('common.message.bookingRemindCreatedSuccessfully')
      });
    } catch (error: any) {
      console.log('error', error);
      notification.error({
        message: error.error || t('common.error')
      });
    }
  };

  return (
    <Modal
      title={t('common.bookingRemind.title')}
      centered
      width={600}
      open={isOpenBookingRemind}
      onCancel={handleCloseModal}
      closeIcon={<IconClose />}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCloseModal}>
          {t('common.actions.cancel')}
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()}>
          {t('common.actions.save')}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('common.bookingRemind.bookingLineId')} name="booking_line_id">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('common.bookingRemind.roomNo')}>
              <Input value={bookingLineDetail?.room_name || ''} disabled />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('common.bookingRemind.remindDate')}
          name="date"
          rules={[
            {
              required: true,
              message: t('common.bookingRemind.remindDateRequired')
            }
          ]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={t('common.time.selectDate')}
          />
        </Form.Item>

        <Form.Item
          label={t('common.bookingRemind.remindContent')}
          name="note"
          rules={[
            {
              required: true,
              message: t('common.bookingRemind.remindContentRequired')
            }
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder={t('common.bookingRemind.remindContentPlaceholder')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default BookingRemindModal;
